import React, { useState, useEffect, useRef } from "react";
import { Calendar } from "primereact/calendar";
import { VALIDATION_MESSAGES } from "../../utils/Constants";
import { onShowCalendarOverlay } from "../../utils/UtilFunctions";
import useCalendarDateEvents from "../common-hooks/useCalendarDateEvents";

const getCalendarValue = ({
  value = "",
  error = false,
  isShowHideError = false,
  isEmptyStringError = false,
}) => {
  if (isEmptyStringError) {
    return new Date();
  }
  return error || !value
    ? error
      ? isShowHideError
        ? value
        : new Date()
      : value
    : new Date(value);
};

const SearchBoxCalendarDate = ({
  name,
  handleSubmit,
  getRef,
  onChange,
  defaultValue,
  errorMessage = "",
}) => {
  const [calendarDateValue, setCalendarDateValue] = useState(
    defaultValue ? { value: defaultValue } : {}
  );

  const [isCalendarPanelVisible, setIsCalendarPanelVisible] = useState(false);

  const [isCalendarFieldError, setIsCalendarFieldError] = useState(false);

  const inputRef = useRef();

  const { onBlur, onFocus, onKeyDown, setCaretPosition, onHide } =
    useCalendarDateEvents(defaultValue, inputRef);

  let calendarSelectButton = null;
  let calendarContainerRef = null;

  const [forceUpdate, setForceUpdate] = useState(false);

  const [isOverlayHidden, setIsOverlayHidden] = useState(false);

  useEffect(() => {
    defaultValue && setCalendarDateValue({ value: defaultValue });
  }, [defaultValue]);

  useEffect(() => {
    if (isOverlayHidden && calendarSelectButton) {
      calendarSelectButton.focus();
      setIsOverlayHidden(false);
    }
  }, [isOverlayHidden]);

  useEffect(() => {
    if (isCalendarPanelVisible) {
      onShowCalendarOverlay(
        getCalendarValue(calendarDateValue),
        calendarContainerRef
      );
    }
  }, [isCalendarPanelVisible]);

  useEffect(() => {
    if (calendarDateValue?.isEmptyStringError) {
      inputRef.current.value = "";
    }
  }, [calendarDateValue?.isEmptyStringError]);

  return (
    <>
      <Calendar
        id={name}
        key={forceUpdate}
        inputId={name}
        inputRef={inputRef}
        showOnFocus={false}
        showButtonBar
        value={getCalendarValue(calendarDateValue)}
        onKeyDown={(e) => {
          onKeyDown(e);
          if (
            e.target.tagName === "INPUT" &&
            e.key === "Enter" &&
            !isCalendarPanelVisible
          ) {
            e.preventDefault();
            const { error = false } = calendarDateValue || {};

            if (error) {
              setIsCalendarFieldError(true);
            } else {
              setIsCalendarFieldError(false);
              handleSubmit();
            }
          }
        }}
        ref={(el) => {
          getRef(el);
          if (el) {
            const calendarBtn = el?.getElement()?.lastChild;
            calendarSelectButton = calendarBtn;
            calendarBtn.tabIndex = 0;
            calendarContainerRef = el;
          }
        }}
        name={name}
        dateFormat="mm/dd/yy"
        keepInvalid
        onChange={(e) => {
          const value = e.value === "//" ? "" : e.value;
          const isInvalidDate = value && !(value instanceof Date);
          inputRef.current.dataset.hasError = isInvalidDate;
          const dateValueObj = {
            error: isInvalidDate,
            value:
              value && !isInvalidDate
                ? value.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })
                : value,
          };
          setCalendarDateValue(dateValueObj);
          onChange(dateValueObj?.value);
          if (dateValueObj.error) {
            inputRef.current.value = dateValueObj.value;
          }
          if (!value) {
            inputRef.current.value = "//";
            setCaretPosition(inputRef.current, 0, 0);
          }
          if (!dateValueObj.error && e.originalEvent.type !== "input") {
            setIsCalendarFieldError(false);
            handleSubmit();
          }
        }}
        onShow={() => {
          setCalendarDateValue({
            value: calendarDateValue.value,
            error: calendarDateValue.error,
            isShowHideError: calendarDateValue.error,
            isEmptyStringError: !calendarDateValue?.value,
          });
          setIsCalendarPanelVisible(true);
          onShowCalendarOverlay(
            getCalendarValue(calendarDateValue),
            calendarContainerRef
          );
        }}
        onHide={() => {
          setIsCalendarPanelVisible(false);
          calendarSelectButton?.focus();
          setForceUpdate((prev) => !prev);
          setIsOverlayHidden(true);
          if (calendarDateValue?.value) {
            setCalendarDateValue({
              value: calendarDateValue.value,
              error: calendarDateValue.error,
              isShowHideError: calendarDateValue.error,
            });
          }

          if (calendarDateValue?.isEmptyStringError) {
            setCalendarDateValue((prev) => ({
              ...prev,
              isEmptyStringError: false,
            }));
          }
          onHide(inputRef.current);
        }}
        showIcon
        invalid={isCalendarFieldError || errorMessage}
        className="width-50"
        onBlur={(e) => {
          if (calendarDateValue?.error) {
            e.target.value = calendarDateValue.value;
            setCalendarDateValue((prev) => ({
              ...prev,
              isShowHideError: true,
            }));
          }
          onBlur(e);
        }}
        onFocus={onFocus}
      />
      {isCalendarFieldError && (
        <small className="p-error" style={{ display: "block" }}>
          {VALIDATION_MESSAGES.INVALID_DATE}
        </small>
      )}
    </>
  );
};

export default React.memo(SearchBoxCalendarDate);